import { Fragment, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { proc } from '@aexae/js_tools';

import { IconButton, Link, Stack, useTheme } from '@mui/material';
import { MIcon } from '../../../@material-extend';
import BreadcrumbItem from './BreadCrumbItem';

import getRoutesAndTitles from './helpers/getRoutesAndTitles';
import useAuth from '../../../../hooks/useAuth';
import { useBreakpoints } from '../../../../hooks/useBreakpoints';
import { useSelector } from '../../../../redux/store';

const Breadcrumbs = ({ themed = false }) => {
  const { isPartner, user } = useAuth();
  const { resourceId } = proc.getQueryParams();
  const { isLargeMobile } = useBreakpoints();
  const history = useNavigate();
  const location = useLocation();
  const { partners: partnerCompanies } = useSelector((state) => state.partners); // partner : partners companies
  const { clockingDeviceSite } = useSelector((state) => state.clockings);
  const [arrayTitles, setArrayTitles] = useState([]);
  const [arrayRoutes, setArrayRoutes] = useState(['/']);
  const [lastSearch, setLastSearch] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const themedBackground = window.env.isMobileDevice || themed;
  const backIconVariant = themedBackground ? 'primary-lightest' : 'primary';
  const { palette } = useTheme();
  const { light, lighter } = palette.primary;

  // Écoute les mises à jour dynamiques du breadcrumb (ex: nom d'utilisateur chargé async)
  useEffect(() => {
    const handler = () => setRefreshKey((k) => k + 1);
    window.addEventListener('breadcrumb:update', handler);
    return () => window.removeEventListener('breadcrumb:update', handler);
  }, []);

  useEffect(() => {
    const { titles, routes } = getRoutesAndTitles(
      isPartner,
      lastSearch,
      partnerCompanies,
      resourceId,
      user,
      setLastSearch,
      { clockingDeviceSiteName: clockingDeviceSite?.name }
    );

    setArrayTitles(titles);
    setArrayRoutes(routes);
  }, [location.pathname, refreshKey, clockingDeviceSite?.name]);

  const handleClickArrow = () => {
    const previousIndex = arrayRoutes.length - 2;
    history(arrayRoutes[previousIndex], { replace: true });
  };

  const handleClickNavigate = (i) => {
    history(arrayRoutes[i]);
  };

  return (
    <Stack id="breadcrumbs" direction="row" alignItems="center" flexGrow={1} gap={1} sx={{ cursor: 'default', gap: 1 }}>
      {/* Android and web browsers already have a back arrow button 
          // TODO : Add this test for - && isIosDevice */}

      {arrayTitles.length > 1 && (
        <IconButton data-cy="navigate-to-last-page" onClick={handleClickArrow}>
          <MIcon icon="arrow_back_ios_new" variant={backIconVariant} />
        </IconButton>
      )}

      {arrayTitles.map((title, i, arrayTitles) => {
        // Last item is the page title
        if (i + 1 === arrayTitles.length) {
          return (
            <BreadcrumbItem key={`${i}-last-item`} text={title} color={themedBackground ? 'white' : 'text.primary'} />
          );
        }

        // On mobile, only page title is rendered
        if (isLargeMobile) {
          return;
        }

        // On larger screens, all hierarchy is visible (and active)
        return (
          <Fragment key={`${i}-link`}>
            <Link
              id={i === 0 ? 'nav-title-space' : undefined}
              component="button"
              onClick={() => handleClickNavigate(arrayTitles[i + 1] ? i + 1 : i)}
            >
              <BreadcrumbItem text={title} color={themedBackground ? lighter : 'primary'} />
            </Link>
            <BreadcrumbItem key={`${i}-separator`} text="/" color={themedBackground ? light : 'text.disabled'} />
          </Fragment>
        );
      })}
    </Stack>
  );
};

Breadcrumbs.propTypes = {
  themed: PropTypes.bool
};

export default Breadcrumbs;
